import { inject, Injectable } from '@angular/core';

import { Action, Selector, State, StateContext } from '@ngxs/store';
import { catchError, tap } from 'rxjs';

import { settingsInitialState, SettingsStateModel } from '@store/models/settings.model';
import { ApiClientService } from '../../core/services/api-client.service';
import { LoadSettings, UpdateSettings, UpdateSpecificTestSetting } from '@store/actions/settings.actions';
import { HttpErrorResponse } from '@angular/common/http';
import { MessageService } from 'primeng/api';
import { TranslateService } from '@ngx-translate/core';
import { Settings } from '@models/settings';

@State<SettingsStateModel>({
  name: 'settings',
  defaults: settingsInitialState,
})
@Injectable()
export class SettingsState {
  private readonly apiClientService = inject(ApiClientService);
  private readonly messageService = inject(MessageService);
  private readonly translate = inject(TranslateService);

  @Selector()
  static settings(state: SettingsStateModel): Settings | null {
    return state.settings;
  }

  @Action(LoadSettings)
  loadSettings(ctx: StateContext<SettingsStateModel>) {
    const state = ctx.getState();
    if (state.isLoaded) {
      return;
    }
    ctx.patchState({ ...state, isLoading: true, isLoaded: false, error: null });

    return this.apiClientService.getSettings().pipe(
      tap((settings) => {
        ctx.patchState({ isLoading: false, settings, isLoaded: true });
      }),
      catchError((error) => {
        ctx.patchState({ isLoading: false, error: error.message });
        throw error;
      }),
    );
  }

  @Action(UpdateSettings)
  updateSettings(ctx: StateContext<SettingsStateModel>, { settings }: UpdateSettings) {
    ctx.patchState({ isLoading: true });
    return this.apiClientService
      .updateSettings(settings)
      .pipe(
        tap(() => {
          // The API might return stale/old values or might not return all fields
          const currentSettings = ctx.getState().settings;

          const mergedSettings = {
            ...currentSettings,
            ...(settings),
            toetsSettings: {
              ...currentSettings.toetsSettings,
              ...(settings.toetsSettings || {}),
            },
          } as Settings;

          // Update settings in state — periods are derived automatically via selector
          ctx.patchState({
            settings: mergedSettings,
            isSaved: true,
            error: null,
            isLoading: false,
          });

          this.messageService.add({
            severity: 'success',
            summary: this.translate.instant('TOASTER_MESSAGES.SETTINGS_SAVED_SUCCESS_TITLE'),
            detail: this.translate.instant('TOASTER_MESSAGES.SETTINGS_SAVED_SUCCESS_MESSAGE'),
          });
        }),
        catchError((error: HttpErrorResponse) => {
          ctx.patchState({ error: error.message, isSaved: false, isLoading: false });
          this.messageService.add({
            severity: 'error',
            summary: this.translate.instant('TOASTER_MESSAGES.SETTINGS_SAVED_ERROR_TITLE'),
            detail: this.translate.instant('TOASTER_MESSAGES.SETTINGS_SAVED_ERROR_MESSAGE', { message: error.message }),
          });
          throw error;
        }),
      );
  }

  @Action(UpdateSpecificTestSetting)
  updateSpecificTestSetting(ctx: StateContext<SettingsStateModel>, setting: UpdateSpecificTestSetting) {
    ctx.patchState({ isLoading: true });
    const currentSettings = ctx.getState().settings;
    const newSettings = {
      ...currentSettings,
      toetsSettings: {
        ...currentSettings.toetsSettings,
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        specifiekeToetsSettings: currentSettings.toetsSettings.specifiekeToetsSettings.map((testSetting: any) => {
          if (testSetting.businessKey === setting.businessKey) {
            return {
              ...testSetting,
              [setting.propertyName]: setting.newVal,
            };
          }
          return testSetting;
        }),
      },
    };
    ctx.dispatch(new UpdateSettings(newSettings));
  }
}
